/**
 * Credential storage.
 *
 * Both keys are long-lived bearer secrets held in `localStorage`, matching the
 * previous frontend's key names so students and TAs stay logged in across the
 * rebuild.
 *
 * Known risk, carried over deliberately: the admin key lives in the same origin
 * as the student app, unscoped and non-expiring, and it grants key minting and
 * challenge deletion. Any XSS here exfiltrates it. Mitigating that properly
 * needs a real session mechanism on the server, which is out of scope; see
 * docs/HANDOVER.md.
 */

const API_KEY_STORAGE = "vis_nav_api_key";
const ADMIN_KEY_STORAGE = "vis_nav_admin_key";
//: Not a credential, but the same mechanism gives it cross-tab sync for free.
const ADMIN_COURSE_STORAGE = "vis_nav_admin_course";

type Listener = () => void;
const listeners = new Set<Listener>();

function emit() {
  listeners.forEach((l) => l());
}

/** Subscribe to credential changes; used by useSyncExternalStore. */
export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  // Keep tabs in sync: logging out in one should log out the others.
  window.addEventListener("storage", listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", listener);
  };
}

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    // Safari in private mode can throw on localStorage access.
    return null;
  }
}

function write(key: string, value: string | null) {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    // Non-fatal: the session simply will not persist across reloads.
  }
  emit();
}

export const apiKeyStore = {
  get: () => read(API_KEY_STORAGE),
  set: (key: string) => write(API_KEY_STORAGE, key.trim()),
  clear: () => write(API_KEY_STORAGE, null),
};

export const adminKeyStore = {
  get: () => read(ADMIN_KEY_STORAGE),
  set: (key: string) => write(ADMIN_KEY_STORAGE, key.trim()),
  clear: () => write(ADMIN_KEY_STORAGE, null),
};

/**
 * Which course the admin views are working on.
 *
 * Challenges, teams and keys all belong to a course, and team names are only
 * unique within one, so every admin view needs to be scoped to exactly one.
 * Holding the choice here rather than in a URL parameter keeps it stable while
 * moving between tabs — switching from Teams to Keys should not silently change
 * which cohort you are editing.
 */
export const adminCourseStore = {
  get: () => read(ADMIN_COURSE_STORAGE),
  set: (courseId: string) => write(ADMIN_COURSE_STORAGE, courseId),
  clear: () => write(ADMIN_COURSE_STORAGE, null),
};

/** What a key looks like while hidden: its length is not a secret, its characters are. */
export function maskKey(key: string): string {
  return "\u2022".repeat(Math.min(key.length, 24));
}
